import { useMemo } from 'react';
import { Box, MenuItem, Paper, TextField } from '@mui/material';
import type { RockBTSGeneral, RockBTSSpecimen } from '../../../types/rockBts';
import { computeBtsSpecimenPreview } from './computations';
import { SPECIMEN_COUNT_OPTIONS } from './rockBtsFields';
// Shared section idiom (reused read-only from the Rock TCS module)
import { inputSx, paperSx, SectionHeader } from '../rock-tcs/sectionStyles';
import { SpecimenParameterCard } from './sections/SpecimenParameterCard';

interface Props {
  general: RockBTSGeneral;
  specimens: RockBTSSpecimen[];
  onGeneralChange: (field: keyof RockBTSGeneral, value: RockBTSGeneral[keyof RockBTSGeneral]) => void;
  onSpecimenChange: (
    specimenNumber: number,
    field: keyof RockBTSSpecimen,
    value: RockBTSSpecimen[keyof RockBTSSpecimen],
  ) => void;
  readOnly?: boolean;
}

/**
 * Tab 1 — Test Parameters (mockup: Rock BTS Test Parameter.png):
 * Test Setup (specimen-count selector + test date, REQ-01/03) and one
 * parameter card per included specimen (REQ-02/04/06).
 */
export const RockBTSParametersTab = ({ general, specimens, onGeneralChange, onSpecimenChange, readOnly }: Props) => {
  const included = useMemo(
    () => specimens.filter((s) => s.specimenNumber <= general.specimenCount),
    [specimens, general.specimenCount],
  );
  const previews = useMemo(() => included.map((s) => computeBtsSpecimenPreview(s)), [included]);

  return (
    <Box sx={{ display: 'grid', rowGap: 1.5 }}>
      <Paper variant="outlined" sx={paperSx}>
        <SectionHeader label="Test Setup" />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, maxWidth: 560 }}>
          <TextField
            select size="small" label="Number of Specimens *"
            value={general.specimenCount}
            onChange={(e) => onGeneralChange('specimenCount', Number(e.target.value))}
            InputProps={{ readOnly }} sx={inputSx}
            helperText="BGC standard is 2; external work may use 1 (REQ-03)"
          >
            {SPECIMEN_COUNT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" label="Test Date *" type="date"
            value={general.testDate ? String(general.testDate).substring(0, 10) : ''}
            onChange={(e) => onGeneralChange('testDate', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: general.specimenCount === 2 ? '1fr 1fr' : '1fr' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        {included.map((s, i) => (
          <SpecimenParameterCard
            key={s.specimenNumber}
            specimen={s}
            preview={previews[i]}
            title={`Specimen ${s.specimenNumber}`}
            onChange={(field, value) => onSpecimenChange(s.specimenNumber, field, value)}
            readOnly={readOnly}
          />
        ))}
      </Box>
    </Box>
  );
};
